import { formatDomainSettings, getDomainSettings } from "common/common.actions";
import jwtDecode from "jwt-decode";
import { actionCreator } from "../common/common.reducer";
import {
  loginUrl,
  userInfoUrl,
  changePasswordUrl,
  domainSettingsUrl,
} from "../common/infrastructure/endpoints";
import { post } from "../common/infrastructure/requests";
import { IDomain, isLandlordTypeGuard } from "../common/models/userInfo";
import { actionCreatorAuth } from "./auth.reducer";
import { Role } from "./types";

export interface ILoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  email: string;
}

export const changePassword =
  (newPassword: string) => async (dispatch: (action: any) => void) => {
    try {
      await post<any, { password: string }>(changePasswordUrl, {
        password: newPassword,
      });
      dispatch(actionCreatorAuth.changePasswordSuccess());
    } catch {
      dispatch(actionCreatorAuth.changePasswordError());
    }
  };

export const login =
  (
    username: string,
    password: string,
    history: any,
    domainCode: string,
    location: any
  ) =>
  async (dispatch: (action: any) => void) => {
    dispatch(actionCreatorAuth.login);
    let details: {
      username: string;
      password: string;
      grant_type: string;
      domain?: string;
      issue_refresh_token: string;
    } = {
      username,
      password,
      grant_type: "password",
      domain: domainCode,
      issue_refresh_token: "true",
    };
    if (!domainCode) {
      delete details.domain;
    }
    try {
      const formBody = [];
      for (const property in details) {
        const encodedKey = encodeURIComponent(property);
        // @ts-ignore
        const encodedValue = encodeURIComponent(details[property]);
        formBody.push(encodedKey + "=" + encodedValue);
      }
      const formData = formBody.join("&");
      const response = await fetch(loginUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });

      if (!response.ok) {
        dispatch(actionCreatorAuth.loginError);
      }

      const body: ILoginResponse = await response.json();
      const tokenDecoded: { exp: number } = jwtDecode(body.access_token);
      if (!domainCode) {
        // Landlord
        dispatch(
          actionCreatorAuth.loginSuccess({
            email: body.email,
            token: body.access_token,
            expiration: tokenDecoded.exp,
            role: domainCode ? Role.Tenant : Role.Landlord,
            refresh_token: body.refresh_token,
          })
        );

        localStorage.setItem("wasUserType", JSON.stringify(Role.Landlord));
        localStorage.removeItem("domainCode");

        if (location?.state?.from) {
          history.push(location.state.from);
          return;
        }
        history.replace("/");
        return;
      }

      if (location?.state?.from) {
        history.push(location.state.from);
        return;
      }

      const userInfoResponse = await fetch(userInfoUrl, {
        headers: {
          Authorization: `Bearer ${body.access_token}`,
        },
      }).then((res) => res.json());

      const domain =
        userInfoResponse &&
        userInfoResponse.Domains &&
        userInfoResponse.Domains.find(
          (d: IDomain) => d.Code.toUpperCase() === domainCode.toUpperCase()
        );

      if (domain?.Code) {
        domainCode = domain.Code;
      }

      let settings = await fetch(domainSettingsUrl(domainCode), {
        headers: {
          Authorization: `Bearer ${body.access_token}`,
        },
      }).then((res) => res.json());
      settings = formatDomainSettings(settings, userInfoResponse);

      if (!settings) {
        dispatch(actionCreatorAuth.mobileAppUnauthorized);
        return;
      }

      dispatch(
        actionCreatorAuth.loginSuccess({
          email: body.email,
          token: body.access_token,
          expiration: tokenDecoded.exp,
          role: domainCode ? Role.Tenant : Role.Landlord,
          refresh_token: body.refresh_token,
        })
      );

      localStorage.setItem("wasUserType", JSON.stringify(Role.Tenant));
      localStorage.setItem("domainCode", JSON.stringify(domain));

      dispatch(actionCreator.setSelectedDomain(domain!));
      dispatch(actionCreator.setDomainSettings(settings));

      const urlSearchParams = new URLSearchParams(location.search);
      const urlId = urlSearchParams.get("id");

      if (!isLandlordTypeGuard(userInfoResponse)) {
        if (urlId) {
          history.replace(`/${domainCode}/profile/changepassword`);
        } else {
          history.replace(
            `/${domainCode}/safetySensors/${userInfoResponse.NodeID}`
          );
        }
      }
    } catch {
      dispatch(actionCreatorAuth.loginError);
    }
  };
